import React from 'react';
import { Helmet } from 'react-helmet';
import { Award, Users, TrendingUp, Heart } from 'lucide-react';
import { achievements } from '../mockData';
import AwardBannersCarousel from '../components/AwardBannersCarousel';

const About = () => {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Hair Extensions Training Academy",
    "description": "Award-winning excellence in hair extensions and hair loss solutions training since 2006. Led by Diane Shawe, training over 4,100 students.",
    "mainEntity": {
      "@type": "EducationalOrganization",
      "name": "Hair Extensions Training Academy",
      "foundingDate": "2006",
      "founder": {
        "@type": "Person",
        "name": "Diane Shawe",
        "jobTitle": "Founder & Lead Trainer"
      },
      "numberOfEmployees": "10-50",
      "award": ["UK Top 50 Salon - HBA Awards 2025", "CPD Accredited Training Provider"],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "4100"
      }
    }
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>About Us | Hair Extensions Training Academy | Diane Shawe</title>
        <meta name="description" content="Award-winning hair extensions training since 2006. Led by Diane Shawe, we've trained over 4,100 students. UK Top 50 Salon, CPD Accredited courses." />
        <link rel="canonical" href="https://hairextensionstraining.academy/about" />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="content-wrapper">
          <h1 className="page-title">About Hair Extensions Training Academy</h1>
          <p className="page-subtitle">
            Award-Winning Excellence in Hair Extensions & Hair Loss Solutions Training Since 2006
          </p>

          <div className="about-hero-banner" data-testid="about-hero-career-banner">
            <img
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/j0qs2p3r_i%20built%20my%20career%20from%20one%20weft.jpeg"
              alt="I built my career from this single weft of hair. Now let me teach you how to build yours. — Diane Shawe, Hair Extensions Training Academy"
              className="about-hero-banner-image"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Diane's Story */}
      <section className="section-container">
        <div className="content-wrapper">
          <div className="story-grid">
            <div className="story-content">
              <h2 className="section-title about-section-title">From High Street to UK Top 50</h2>
              
              {/* Video */}
              <div className="about-video-wrapper">
                <div className="about-video-container">
                  <video
                    controls
                    preload="metadata"
                    className="about-video-player"
                  >
                    <source src="https://customer-assets.emergentagent.com/job_extension-academy/artifacts/on0nsbwy_Hair%20extensions%20training%20coursestraining%20courses_20260109_152438_0008.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
              
              <p className="section-text about-text-centered">
                Diane Shawe's journey is one of passion, perseverance, and transformation. Starting with a vision
                to provide exceptional hair services, Diane built her salon from the ground up, overcoming challenges
                and adversity to become one of the UK's most recognized names in hair extensions and hair loss solutions.
              </p>
              <p className="section-text">
                Through dedication and an unwavering commitment to excellence, Diane's salon achieved recognition as
                one of the UK's Top 50 salons at the prestigious HBA Awards 2025. Her expertise extends beyond salon
                services—she's a pioneer in developing innovative hair extension techniques and training methodologies.
              </p>
              <p className="section-text">
                Despite facing the unprecedented challenges of the COVID-19 pandemic, Diane's resilience and the support
                of her community enabled her to not only survive but thrive. Today, she leads a globally recognized training
                academy that has transformed the careers of over 4,100 students worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diane Shawe Intro Video */}
      <section className="section-container about-intro-video-section" data-testid="about-intro-video-section">
        <div className="content-wrapper">
          <div className="about-intro-video">
            <video
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/ja84kj8s_WhatsApp%20Video%202026-06-30%20at%2014.42.13.mp4"
              controls
              playsInline
              preload="metadata"
              className="about-intro-video-player"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="section-container section-alt">
        <div className="content-wrapper">
          <h2 className="section-title text-center-content">Awards & Recognition</h2>
          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <div key={index} className="achievement-card">
                <Award className="achievement-icon" size={24} />
                <p className="achievement-text">{achievement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Award Banners Carousel */}
      <section className="section-container">
        <div className="content-wrapper">
          <a
            href="https://www.qualiquiz.app/quiz/which-hair-extension-course-is-right-for-you-take-60-second-assessment"
            target="_blank"
            rel="noopener noreferrer"
            className="about-quiz-banner"
            data-testid="about-quiz-banner"
            aria-label="Take the 60-second career assessment: Which hair extension course is right for you?"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/mf9808od_WhatsApp%20Image%202026-06-30%20at%2015.44.41%20%2811%29.jpeg"
              alt="Hair Extensions Training Academy — Over 15 million people in the UK experience hair loss. Take the career assessment."
              className="about-quiz-banner-image"
              loading="lazy"
            />
          </a>

          <h2 className="section-title text-center-content">Our Journey of Excellence</h2>
          <AwardBannersCarousel />
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section-container">
        <div className="content-wrapper">
          <div className="values-grid">
            <div className="value-card">
              <Heart className="value-icon" size={40} />
              <h3 className="value-title">Empowering</h3>
              <p className="value-text">
                We empower individuals to transform their careers and build successful businesses in the thriving
                hair extensions industry.
              </p>
            </div>
            <div className="value-card">
              <Users className="value-icon" size={40} />
              <h3 className="value-title">Inclusive</h3>
              <p className="value-text">
                Our training is designed for salon professionals, mobile technicians, wellness providers, and
                aspiring franchisees from all backgrounds.
              </p>
            </div>
            <div className="value-card">
              <TrendingUp className="value-icon" size={40} />
              <h3 className="value-title">Progressive</h3>
              <p className="value-text">
                We stay at the forefront of industry trends and techniques, ensuring our students receive the most
                current and relevant training.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Students Training Video */}
      <section className="section-container about-students-video-section" data-testid="about-students-video-section">
        <div className="content-wrapper">
          <div className="about-intro-video">
            <video
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/bf85vc3s_students%20trainingnhai%20extensions%202.mp4"
              controls
              playsInline
              preload="metadata"
              className="about-intro-video-player"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="section-container cta-section">
        <div className="content-wrapper">
          <div className="impact-stats">
            <div className="impact-stat">
              <h3 className="impact-number">4,100+</h3>
              <p className="impact-label">Students Trained Since 2006</p>
            </div>
            <div className="impact-stat">
              <h3 className="impact-number">19</h3>
              <p className="impact-label">Years of Excellence</p>
            </div>
            <div className="impact-stat">
              <h3 className="impact-number">£10B</h3>
              <p className="impact-label">Global Market Opportunity</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;